// ============================================
// AUTOMATISATION — CATALOGUE, MODÈLES ET LIBELLÉS
// ============================================
// Le serveur n'accepte que les déclencheurs et actions réellement exécutés.
// Il en publie la liste (`meta.catalogue` de GET /admin/automation/rules) ;
// la liste ci-dessous n'est qu'un repli si elle manque. Toute règle hors
// catalogue est refusée (400, code REGLE_NON_PRISE_EN_CHARGE).

export interface CatalogueAutomatisation {
  declencheurs: string[];
  actions: string[];
  /** Libellés publiés par le serveur (code → libellé) ; priment sur les libellés connus. */
  libelles?: Record<string, string>;
}

export const CATALOGUE_PAR_DEFAUT: CatalogueAutomatisation = {
  declencheurs: ['TICKET_CREATED', 'TICKET_UPDATED', 'TICKET_STATUS_CHANGED', 'TICKET_RESOLVED', 'TICKET_CLOSED'],
  actions: ['assign.least_workload', 'assign.by_skill', 'notify.supervisor', 'notify.team', 'notify.assigned', 'escalate', 'close'],
  libelles: {},
};

export const CODE_REGLE_NON_PRISE_EN_CHARGE = 'REGLE_NON_PRISE_EN_CHARGE';

export const TRIGGER_LABELS: Record<string, string> = {
  TICKET_CREATED: 'Création de dossier',
  TICKET_UPDATED: 'Mise à jour de dossier',
  TICKET_STATUS_CHANGED: 'Changement de statut',
  TICKET_RESOLVED: 'Dossier résolu',
  TICKET_CLOSED: 'Dossier fermé',
};

export const ACTION_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  'assign.least_workload': { label: 'Assigner (moins de charge)', icon: 'user', color: 'blue' },
  'assign.by_skill': { label: 'Assigner (par compétence)', icon: 'user', color: 'blue' },
  'notify.supervisor': { label: 'Notifier superviseur', icon: 'bell', color: 'purple' },
  'notify.team': { label: 'Notifier équipe', icon: 'users', color: 'purple' },
  'notify.assigned': { label: 'Notifier agent assigné', icon: 'bell', color: 'purple' },
  'escalate': { label: 'Escalader', icon: 'alert', color: 'red' },
  'close': { label: 'Fermer le dossier', icon: 'check', color: 'green' },
};

export interface ModeleAutomatisation {
  id: string;
  name: string;
  description: string;
  /** Code du déclencheur côté serveur. */
  trigger: string;
  conditions: string[];
  actions: string[];
}

export const AUTOMATION_TEMPLATES: ModeleAutomatisation[] = [
  { id: 'tpl1', name: 'Auto-assignation par compétence', description: 'Assigne les dossiers techniques aux agents techniques', trigger: 'TICKET_CREATED', conditions: ['issueType = TECHNICAL'], actions: ['assign.by_skill'] },
  { id: 'tpl2', name: 'Auto-assignation au moins chargé', description: "Confie chaque nouveau dossier à l'agent qui en a le moins", trigger: 'TICKET_CREATED', conditions: [], actions: ['assign.least_workload'] },
  { id: 'tpl3', name: 'Urgences au superviseur', description: 'Prévient le superviseur à la création d\'un dossier urgent', trigger: 'TICKET_CREATED', conditions: ['priority = URGENT'], actions: ['notify.supervisor'] },
  { id: 'tpl4', name: "Agent prévenu du changement de statut", description: "Notifie l'agent assigné quand le statut du dossier change", trigger: 'TICKET_STATUS_CHANGED', conditions: [], actions: ['notify.assigned'] },
];

/**
 * Extrait un code d'une entrée de catalogue : chaîne, ou objet. Le serveur
 * (catalogueAutomatisations) publie les déclencheurs en { valeur, libelle }
 * et les actions en { type, libelle }.
 */
function codeEntree(e: unknown): string | null {
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') {
    const o = e as Record<string, unknown>;
    for (const k of ['valeur', 'code', 'value', 'type', 'trigger', 'id']) if (typeof o[k] === 'string') return o[k] as string;
  }
  return null;
}

function codes(liste: unknown): string[] | null {
  if (!Array.isArray(liste)) return null;
  const r = liste.map(codeEntree).filter((c): c is string => !!c);
  return r.length > 0 ? r : null;
}

/** Libellés { code → libelle } des entrées en objet. */
function libellesDe(liste: unknown, cible: Record<string, string>): void {
  if (!Array.isArray(liste)) return;
  for (const e of liste) {
    const code = codeEntree(e);
    if (!code || !e || typeof e !== 'object') continue;
    const o = e as Record<string, unknown>;
    const libelle = o.libelle ?? o.label;
    if (typeof libelle === 'string' && libelle.trim()) cible[code] = libelle;
  }
}

/**
 * Lit le catalogue publié par le serveur ; accepte `declencheurs`/`triggers`
 * et `actions`, en chaînes ou en objets, et garde leurs libellés. Repli sur
 * la liste connue.
 */
export function lireCatalogue(brut: unknown): CatalogueAutomatisation {
  if (!brut || typeof brut !== 'object') return CATALOGUE_PAR_DEFAUT;
  const o = brut as Record<string, unknown>;
  const listeDeclencheurs = o.declencheurs ?? o.triggers ?? o.declencheur;
  const listeActions = o.actions ?? o.action;
  const libelles: Record<string, string> = {};
  libellesDe(listeDeclencheurs, libelles);
  libellesDe(listeActions, libelles);
  return {
    declencheurs: codes(listeDeclencheurs) ?? CATALOGUE_PAR_DEFAUT.declencheurs,
    actions: codes(listeActions) ?? CATALOGUE_PAR_DEFAUT.actions,
    libelles,
  };
}

/** Libellé d'un déclencheur ou d'une action : celui du serveur, sinon `repli`. */
export function libelleCatalogue(catalogue: CatalogueAutomatisation, code: string, repli: string): string {
  return catalogue.libelles?.[code] || repli;
}

/** Une règle existante utilise-t-elle un déclencheur ou une action hors catalogue ? */
export function regleHorsCatalogue(
  regle: { trigger: string; actions: Array<{ type: string }> },
  catalogue: CatalogueAutomatisation,
): boolean {
  return !catalogue.declencheurs.includes(regle.trigger)
    || regle.actions.some(a => !catalogue.actions.includes(a.type));
}

/** Modèles entièrement couverts par le catalogue. */
export function modelesDisponibles(catalogue: CatalogueAutomatisation, modeles = AUTOMATION_TEMPLATES): ModeleAutomatisation[] {
  return modeles.filter(m => catalogue.declencheurs.includes(m.trigger) && m.actions.every(a => catalogue.actions.includes(a)));
}
